import React, { useMemo } from "react";
import { Alert, Button, Input, Select, Table } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import useLang from "../../../hooks/useLang";
import { PayableDriverBalance } from "../../../api/driver-salary-payouts";
import { thousandsFormatter } from "../../../utils/formatters";
import PayoutDisclosure from "./PayoutDisclosure";
import PayoutStat from "./PayoutStat";
import { driverFullName, MAX_DESCRIPTION_LENGTH } from "./payoutFormat";

interface PayoutConfigureStepProps {
	drivers: PayableDriverBalance[];
	included: PayableDriverBalance[];
	/** In the order they were excluded, most recent first. */
	excluded: PayableDriverBalance[];
	total: number;
	description: string;
	onExclude: (driverId: number) => void;
	onInclude: (driverId: number) => void;
	onDescriptionChange: (value: string) => void;
}

const driverDetails = (driver: PayableDriverBalance) =>
	[driver.city, driver.phone].filter(Boolean).join(" · ");

const PayoutConfigureStep = ({
	drivers,
	included,
	excluded,
	total,
	description,
	onExclude,
	onInclude,
	onDescriptionChange,
}: PayoutConfigureStepProps) => {
	const { strings } = useLang("driver");
	const copy = strings?.bulkPayout;

	// Only drivers still being paid can be excluded. searchText carries everything the admin may
	// type — two drivers can share a name, so phone and code must match too.
	const excludeOptions = useMemo(
		() =>
			included.map((driver) => ({
				value: driver.driverId,
				searchText: `${driverFullName(driver)} ${driver.phone ?? ""} ${
					driver.driverId
				}`.toLowerCase(),
				label: (
					<div className="flex items-center justify-between gap-3">
						<span className="truncate">
							{driverFullName(driver)}
							{driver.city && (
								<span className="text-gray-400">
									{" · "}
									{driver.city}
								</span>
							)}
						</span>
						<span className="shrink-0 text-gray-600">
							{thousandsFormatter(driver.balance)}
						</span>
					</div>
				),
			})),
		[included]
	);

	return (
		<div className="flex flex-col gap-5 py-2">
			<Alert
				type="info"
				showIcon
				message={
					copy?.infoMessage ??
					"Payment will be made to every driver with a balance. To exclude a driver, select them in the filter below."
				}
			/>

			<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
				<PayoutStat
					label={copy?.statWithBalance ?? "Drivers with a balance"}
					value={drivers.length}
				/>
				<PayoutStat
					label={copy?.statToPay ?? "Will be paid"}
					value={included.length}
				/>
				<PayoutStat
					label={copy?.statTotal ?? "Total to pay"}
					value={thousandsFormatter(total)}
					highlight
				/>
			</div>

			<div>
				<div className="mb-1.5 text-[15px] font-medium text-gray-800">
					{copy?.excludeLabel ?? "Exclude drivers"}
				</div>
				{/* Search-and-add: the value stays empty, picking a driver moves them to the list below. */}
				<Select<number | null>
					className="w-full"
					showSearch
					value={null}
					placeholder={
						copy?.excludePlaceholder ??
						"Search by name, phone or code"
					}
					options={excludeOptions}
					filterOption={(input, option) =>
						(option?.searchText ?? "").includes(
							input.trim().toLowerCase()
						)
					}
					onSelect={(driverId) => {
						if (driverId !== null) onExclude(driverId);
					}}
					notFoundContent={copy?.noMatch ?? "No driver found"}
				/>
			</div>

			<div>
				<div className="mb-1.5 text-[15px] font-medium text-gray-800">
					{`${copy?.excludedTitle ?? "Excluded drivers"} (${
						excluded.length
					})`}
				</div>
				{excluded.length === 0 ? (
					<div className="text-sm text-gray-500">
						{copy?.noExcluded ??
							"No driver is excluded — everyone above will be paid."}
					</div>
				) : (
					<ul className="max-h-60 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
						{excluded.map((driver) => (
							<li
								key={driver.driverId}
								className="flex items-center justify-between gap-3 px-4 py-2.5"
							>
								<div className="min-w-0">
									<div className="truncate text-[15px] text-gray-900">
										{driverFullName(driver)}
									</div>
									<div className="truncate text-sm text-gray-500">
										{driverDetails(driver)}
									</div>
								</div>
								<div className="flex shrink-0 items-center gap-3">
									<span className="text-[15px] text-gray-500 line-through">
										{thousandsFormatter(driver.balance)}
									</span>
									<Button
										type="text"
										icon={<CloseOutlined />}
										onClick={() =>
											onInclude(driver.driverId)
										}
									>
										{copy?.remove ?? "Remove"}
									</Button>
								</div>
							</li>
						))}
					</ul>
				)}
			</div>

			<PayoutDisclosure
				title={`${copy?.includedTitle ?? "Drivers who will be paid"} (${
					included.length
				})`}
			>
				<Table<PayableDriverBalance>
					size="middle"
					rowKey="driverId"
					dataSource={included}
					pagination={false}
					scroll={{ y: 300 }}
					columns={[
						{
							title: copy?.colDriver ?? "Driver",
							key: "name",
							render: (_, driver) => driverFullName(driver),
						},
						{
							title: copy?.colCity ?? "City",
							dataIndex: "city",
						},
						{
							title: copy?.colBalance ?? "Balance",
							dataIndex: "balance",
							align: "right",
							render: (balance: number) =>
								thousandsFormatter(balance),
						},
						{
							key: "exclude",
							width: 130,
							align: "right",
							render: (_, driver) => (
								<Button
									type="link"
									onClick={() => onExclude(driver.driverId)}
								>
									{copy?.exclude ?? "Exclude"}
								</Button>
							),
						},
					]}
				/>
			</PayoutDisclosure>

			<div>
				<div className="text-[15px] font-medium text-gray-800">
					{copy?.descriptionLabel ?? "Description"}{" "}
					<span className="text-red-500">*</span>
				</div>
				<div className="mb-1.5 text-sm text-gray-500">
					{copy?.descriptionNote ??
						"The same description is saved for every driver, together with your name."}
				</div>
				<Input.TextArea
					rows={3}
					value={description}
					maxLength={MAX_DESCRIPTION_LENGTH}
					showCount
					onChange={(e) => onDescriptionChange(e.target.value)}
					placeholder={
						copy?.descriptionPlaceholder ??
						"e.g. Driver salaries – September 2026"
					}
				/>
			</div>
		</div>
	);
};

export default PayoutConfigureStep;
